import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject, of, forkJoin } from 'rxjs';
import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { SessionService } from '../../../core/services/session.service';
import { FactoryhelperService } from '../../../shared/services/factoryHelper.service';
import { productDetail, ProductList } from '../../../shared/models/ProductDetailsModel';
import { ProductTypes } from '../../../shared/models/ProductTypes';
import { BranchType } from '../../../shared/models/BranchTypes';
import { HttpHeaders } from '@angular/common/http';
import { ProductAvailability } from '../../../shared/models/ProductAvailability';
import { PlatformCode } from '../../../shared/models/PlatformCode';
import { DatePipe } from '@angular/common';
import { BrokerInfo } from '../../../shared/models/BrokerInfo';
import { map } from 'rxjs/operators';
import { Product } from '../../../shared/models/ProductModel';
import { SimulationConstantsService } from './simulation-constants.service';
import { DistributionChannel } from '../../../shared/models/distributionChannelType';
import { ProductCodeRange } from '../../../shared/models/LeadProducts';
import { ResourceModel } from '../../../shared/models/ResourceModel';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { StartParameter } from '../../../shared/models/StartParameter';

@Injectable({
  providedIn: 'root'
})
export class UserSimulationService {

  private apiUrl: string;
  private ProductDetailList: Array<ProductList>;
  PlatformCode: PlatformCode;

  private isBudgetFieldsValid = new BehaviorSubject<boolean>(true);
  private branchType = new Subject<string>();

  private ProductTypes = ProductTypes;
  private ProductAvailability = ProductAvailability;

  private BrokerData: BrokerInfo;

  IsEmailFlowForEdit: boolean;
  StarFlyRecord: productDetail;
  AGFundDynamic: productDetail;
  constructor(
    private readonly translateService: AgTranslateService,
    private readonly session: SessionService,
    public readonly factoryhelperService: FactoryhelperService,
    private readonly datePipe: DatePipe,
    private readonly constants: SimulationConstantsService
  ) {
    this.apiUrl = AgAppConfigService.get<string>('applicativeApiUrl');
  }
  partnerRangecode: ProductCodeRange;
  getProducts(duration: number): Observable<Product[]> {
    const [culture, context] = this.getCultureWithContext();
    const productUrl = `${this.apiUrl}/${culture}/silruleengine/context/${context}/productRangeCode/${this.session.getUserParams().partnercodeRange}/duration/${duration}/getpossibleproducts`;
    return this.factoryhelperService.get<{ Result: Product[] }>(productUrl).pipe(map(x => x.Result));
  }

  getProductDetails(queryObject: {Context: number, Duration: number, MonthlyAmount: number, OneShotAmount: number, ProductRangeCode: number}) {
    const [culture, context] = this.getCultureWithContext();
    const queryString = Object.keys(queryObject).map(key => key + '=' + queryObject[key]).join('&');
    const productDetailsUrl = `${this.apiUrl}/${culture}/silruleengine/calcsilprojection?${queryString}`;
    let querString_getpossibleproduct: URLSearchParams | string;
    if (context == DistributionChannel.Broker) {
      querString_getpossibleproduct = new URLSearchParams({
        monthlyAmount: (queryObject?.MonthlyAmount !== null) ? queryObject?.MonthlyAmount.toString() : '0',
        oneShotAmount: (queryObject?.OneShotAmount !== null) ? queryObject?.OneShotAmount.toString() : '0'
      });

    } else {
      querString_getpossibleproduct = "monthlyAmount=0&oneShotAmount=0"
    }
    const productUrl = `${this.apiUrl}/${culture}/silruleengine/context/${context}/productRangeCode/${queryObject.ProductRangeCode}/duration/${queryObject.Duration}/getpossibleproducts?${querString_getpossibleproduct.toString()}`;
    return forkJoin([
      this.factoryhelperService.get<{ Result: Product[] }>(productUrl).pipe(map(x => x?.Result)),
      this.factoryhelperService.get<{ Result: { SimulationList: productDetail[] }}>(productDetailsUrl).pipe(map(x => x?.Result?.SimulationList))
    ]);
  }

  public getStartParametersForValidation(partnerId?: string | number, productCode?: string | number): Observable<{ Result: StartParameter }> {
    const [culture, context] = this.getCultureWithContext();
    const simulationDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    let productCodeRange;

    const userParams = this.session.getUserParams();

    if (userParams) {
      if (userParams.distributionChannel === DistributionChannel.Broker) {
        productCodeRange = ProductCodeRange.Uppie // 1
      } else {
        productCodeRange = productCode ?? (isNaN(userParams.partnercodeRange) ? 0 : userParams.partnercodeRange)
      }
    }

    let knownBrokerId: string | number;
    if (partnerId) {
      knownBrokerId = partnerId
    } else {
      knownBrokerId = userParams?.partnercode ?? 0;
    }
    const processType = productCodeRange === ProductCodeRange.Yongo ? 'U' : 'S'
    // 'bc255160-96b9-4da2-b0ad-8abc8307b62d' for broker with no logo // '0e17441c-70eb-40a5-8c79-39a512ef0e15' for broker with logo;
    const parameterUrl = `${this.apiUrl}/${culture}/silparameter/context/${context}/date/${simulationDate}/processtype/${processType}/brokerId/${knownBrokerId}/productrangecode/${Number(productCodeRange)}/getvalidationparameters`;
    return this.factoryhelperService.get(parameterUrl);
    // eslint-disable-next-line max-len
    //return of({"Result": {"MinDuration":1.0,"MaxDuration":20.0,"MinOneShotPremium":10.00,"MaxOneShotPremium":60000.00,"MinRecurrentPremium":10.00,"MaxRecurrentPremium":1125.00,"PartnerLogo":"https://aagjebusd000.blob.core.windows.net/silapplication/AG4you_Def.svg?sv=2016-05-31&sr=b&sig=hk9UmkZvIER%2BoWvW0Ctm5JIiYcZgffe9Xus8g7pm%2BGQ%3D&st=2022-09-21T10%3A34%3A09Z&se=2022-09-21T20%3A49%3A09Z&sp=r","PartnerLinkNL":null,"PartnerLinkFR":null,"IsPartnerExist":true,"BrokerInfo":null,"IsPlanDistributor":false,"Partner":"AG 4 YOU","IsPartnerAllowedToDistribute":true}});
  }

  setBudgetFormValidity(flag: boolean) {
    this.isBudgetFieldsValid.next(flag);
  }
  getBudgetFormValidity(): Observable<boolean> {
    return this.isBudgetFieldsValid.asObservable();
  }
  setAllProductsdata(data: Array<ProductList>) {
    data.forEach(d => {
      // keyname is the productname set in the enum
      const getKey = Object.keys(this.ProductTypes).find(key => this.ProductTypes[key] === d.SmartInvestLineProductTypeCode);
      // now the same keyname holds the minimum duration value so fetching the duration with it
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const productMinimumDuration = this.ProductAvailability[getKey];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      d.MinimumDuration = productMinimumDuration;
      // checking the user provided duration is makes the product available or not
      if (Number(this.session.getSimulationData().duration) >= productMinimumDuration) {
        d.IsAvailable = true;
      } else {
        d.IsAvailable = false;
      }
    });
    this.ProductDetailList = data;
    // ordering the products in the order of dynamic->Neutral->Stable->Safe
    this.ProductDetailList.sort((a, b) => {
      return (b.SmartInvestLineProductTypeCode - a.SmartInvestLineProductTypeCode);
    });
  }
  getAllProductsData() {
    if (this.ProductDetailList && this.ProductDetailList.length) {
      return this.ProductDetailList;
    }
    return null;
  }

  getSelectedProductDetails(productId: ProductTypes): ProductList {
    for (const product of this.ProductDetailList) {
      if (product.SmartInvestLineProductTypeCode === productId) {
        return product;
      }
    }
    return undefined;
  }

  setSelectedBranchType(type: string) {
    this.branchType.next(type);
  }

  getSelectedBranchType(): Observable<string> {
    return this.branchType.asObservable();
  }

  getBranchTypeofProduct(branch: string) {
    if (branch === '21') {
      return BranchType.BR21;
    }
    else {
      return BranchType.BR23;
    }
  }

  getDocument(url: string) {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/pdf').append('Accept', 'application/pdf').append('Access-Control-Allow-Origin', '*'),
      responseType: 'blob',
      observe: 'body'
    };
    return this.factoryhelperService.get(url, options as unknown); // TODO check
  }

  getCultureWithContext() {
    return [this.translateService.currentLang, this.session.getUserParams().distributionChannel];
  }

  getBrokerContent(queryObject: { brokerId: string, isPublicId: boolean }): Observable<BrokerInfo> {
    const [culture] = this.getCultureWithContext();
    const queryString = Object.keys(queryObject).map(key => key + '=' + queryObject[key]).join('&');
    const brokerInfourl = `${this.apiUrl}/${culture}/silbrokersearch/getbrokerinfo?${queryString}`;
    return this.factoryhelperService.get<{ Result: BrokerInfo}>(brokerInfourl).pipe(map(x => x.Result));
  }

  postDigitalContactDetails(contactDetails) {
    const [culture] = this.getCultureWithContext();
    const prospectCreationUrl = `${this.apiUrl}/${culture}/manageuser/storeprospect`;
    return this.factoryhelperService.post(prospectCreationUrl, contactDetails);
  }

  sendBrokerEmail(mailContent) {
    const [culture] = this.getCultureWithContext();
    const sendBrokerEmailUrl = `${this.apiUrl}/${culture}/communication/sendbrokeremail`;
    return this.factoryhelperService.post(sendBrokerEmailUrl, mailContent);
  }

  sendDigitalEmail(digitalEmail) {
    const [culture] = this.getCultureWithContext();
    const prospectCreationUrl = `${this.apiUrl}/${culture}/communication/sendagdigitalemail`;
    return this.factoryhelperService.post(prospectCreationUrl, digitalEmail);
  }

  validateReCaptcha(captchaDetails) {
    const [culture] = this.getCultureWithContext();
    const validateCaptchaUrl = `${this.apiUrl}/${culture}/manageuser/validatecaptchacontactdetails`;
    return this.factoryhelperService.post(validateCaptchaUrl, captchaDetails);
  }
  getFirstCharCaseWord(word: string) {
    if (!word) {
      return word;
    }
    if (word.length === 1) {
      return word.toUpperCase();
    }
    return word[0].toUpperCase() + word.substring(1);
  }

  mergeProductData(products: Product[], productDetails: productDetail[]) {
    const userSurvey = sessionStorage.getItem('userSurvey');
    this.partnerRangecode = this.session.getUserParams().distributionChannel === DistributionChannel.Broker ? 1 :
      this.session.getUserParams().partnercodeRange ? this.session.getUserParams().partnercodeRange :
        ((JSON.parse(atob(userSurvey)) as {partnerCodeRange : ProductCodeRange}).partnerCodeRange);
    const arr: ProductList[] = [];
    let gainLossValueArr: number[] = [];
    productDetails.forEach(x => {
      const matchedProduct = products.find(product => x.SmartInvestLineProductTypeCode === product.SmartInvestLineProductTypeCode);
      const matchedProductScenarios = this.constants.INCOME_PERCENTAGE.find(product => x.SmartInvestLineProductTypeCode === (product.SmartInvestLineProductTypeCode as ProductTypes));
      arr.push({ ...matchedProduct, ...x, ...matchedProductScenarios });
      gainLossValueArr = [...gainLossValueArr, ...[Math.abs(x.GainLossScenarioList[0].Favorable), Math.abs(x.GainLossScenarioList[0].Intermediary), Math.abs(x.GainLossScenarioList[0].Stress), Math.abs(x.GainLossScenarioList[0].Unfavorable)]];
    });
    const maxAbsoluteGainLossValue = Math.max(...gainLossValueArr);
    if (this.session.getUserParams().distributionChannel === DistributionChannel.AG) { // Uppie Flow Products
      if (this.partnerRangecode === ProductCodeRange.Uppie) {

        this.StarFlyRecord = productDetails.find(x => (x.SmartInvestLineProductTypeCode as number) === 7); // Uppi Star Fly
        arr.forEach(y => {
          const Favourable = Math.abs(((y.GainLossScenarioList[0].Favorable / maxAbsoluteGainLossValue) * 0.95) * 100)  //[Return in favorable scenario of each product / Max return of all scenarios of all the products] * 0,95
          y.calculation.positive = Favourable;
          if (Favourable < 1) {
            y.positive = "1";
          } else {
            const FavourableLength = Math.trunc(Favourable);
            y.positive = FavourableLength.toString();
          }
          const UnFavorable = Math.abs(((y.GainLossScenarioList[0].Unfavorable / maxAbsoluteGainLossValue) * 0.95) * 100)  //[Return in unfavorable scenario of each product / Max return of all scenarios of all the products] * 0,05 
          y.calculation.negative = UnFavorable;
          if (UnFavorable < 1) {
            y.negative = "1";
          } else {
            const UnFavourableLength = Math.trunc(UnFavorable);
            y.negative = UnFavourableLength.toString();
          }
          const Intermediate = ((y.GainLossScenarioList[0].Intermediary / maxAbsoluteGainLossValue) * 0.95) * 100  //[Return in Intermediate scenario of each product / Max return of all scenarios of all the products] * 0,05 
          y.calculation.intermediary = Intermediate;

          const stress = ((y.GainLossScenarioList[0].Stress / maxAbsoluteGainLossValue) * 0.95) * 100  //[Return in stress scenario of each product / Max return of all scenarios of all the products] * 0,05 
          y.calculation.tension = stress;
        });
      }
    }
    if (this.session.getUserParams().distributionChannel === DistributionChannel.Broker) {// Broker Flow Products
      this.AGFundDynamic = productDetails.find(x => x.SmartInvestLineProductTypeCode === ProductTypes.AGFundDynamic);
      arr.forEach(y => {
        const Favourable = Math.abs(((y.GainLossScenarioList[0].Favorable / maxAbsoluteGainLossValue) * 0.95) * 100)  //[Return in favorable scenario of each product / Max return of all scenarios of all the products] * 0,95
        y.calculation.positive = Favourable;
        if (Favourable < 1) {
          y.positive = "1";
        } else {
          const FavourableLength = Math.trunc(Favourable);
          y.positive = FavourableLength.toString();
        }
        const UnFavorable = Math.abs(((y.GainLossScenarioList[0].Unfavorable / maxAbsoluteGainLossValue) * 0.95) * 100) //[Return in unfavorable scenario of each product / Max return of all scenarios of all the products] * 0,05 
        y.calculation.negative = UnFavorable;
        if (UnFavorable < 1) {
          y.negative = "1";
        } else {
          const UnFavourableLength = Math.trunc(UnFavorable);
          y.negative = UnFavourableLength.toString();
        }
        const Intermediate = ((y.GainLossScenarioList[0].Intermediary / maxAbsoluteGainLossValue) * 0.95) * 100  //[Return in Intermediate scenario of each product / Max return of all scenarios of all the products] * 0,05 
        y.calculation.intermediary = Intermediate;
        const stress = ((y.GainLossScenarioList[0].Stress / maxAbsoluteGainLossValue) * 0.95) * 100  //[Return in stress scenario of each product / Max return of all scenarios of all the products] * 0,05 
        y.calculation.tension = stress;
      });
    }
    this.setAllProductsdata(arr);
    return of(arr);
  }
  getResourceFromAPI(): Observable<ResourceModel[]> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcommon/resources`;
    return this.factoryhelperService.get<{ Result: ResourceModel[] }>(url).pipe(map(x => x.Result));
  }
}
